import { createEvent, createStore } from 'effector'

const DEFAULT_ZOOM_FACTOR = 0.9

const UI_ZOOM_FACTORS = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
]

const zoomFactorToUIPercentage = (zoomFactor: number): number => {
  return (zoomFactor / DEFAULT_ZOOM_FACTOR) * 100
}

const ZOOM_STORAGE_KEY = 'browser-zoom-factor'

const saveZoomToStorage = (zoomFactor: number) => {
  try {
    localStorage.setItem(ZOOM_STORAGE_KEY, zoomFactor.toString())
  } catch (error) {
    console.warn('Failed to save zoom factor to localStorage:', error)
  }
}

export const loadZoomFromStorage = () => {
  try {
    const stored = localStorage.getItem(ZOOM_STORAGE_KEY)

    if (stored) {
      const parsed = parseFloat(stored)

      if (!isNaN(parsed) && UI_ZOOM_FACTORS.includes(parsed)) {
        return parsed
      }
    }
  } catch (error) {
    console.warn('Failed to load zoom factor from localStorage:', error)
  }

  return window.zoomFactor || DEFAULT_ZOOM_FACTOR
}

export const setZoomControlsOpen = createEvent<boolean>(
  'set zoom controls open',
)

export const $isOpen = createStore(false, { name: 'zoom controls open' }).on(
  setZoomControlsOpen,
  (_, payload) => payload,
)

export const setZoom = createEvent<number>('set zoom factor')
export const resetZoom = createEvent<unknown>('reset zoom factor')
export const increaseZoom = createEvent<unknown>('increase zoom factor')
export const decreaseZoom = createEvent<unknown>('decrease zoom factor')

export const $zoomFactor = createStore<number>(loadZoomFromStorage(), {
  name: 'zoom factor',
})
  .on(setZoom, (_, value) => {
    return UI_ZOOM_FACTORS.includes(value) ? value : DEFAULT_ZOOM_FACTOR
  })
  .on(increaseZoom, (state) => {
    const index = UI_ZOOM_FACTORS.findIndex((item) => item === state)
    const nextIndex = Math.min(index + 1, UI_ZOOM_FACTORS.length - 1)

    return UI_ZOOM_FACTORS[nextIndex]
  })
  .on(decreaseZoom, (state) => {
    const index = UI_ZOOM_FACTORS.findIndex((item) => item === state)
    const nextIndex = Math.max(index - 1, 0)

    return UI_ZOOM_FACTORS[nextIndex]
  })
  .on(resetZoom, () => DEFAULT_ZOOM_FACTOR)

export const $uiZoomFactor = $zoomFactor.map(zoomFactorToUIPercentage)

export { DEFAULT_ZOOM_FACTOR, UI_ZOOM_FACTORS, zoomFactorToUIPercentage }

$zoomFactor.watch((zoomFactor) => {
  saveZoomToStorage(zoomFactor)
})

const CLOSE_TIMEOUT = 3000
let timeoutId: number | null = null

window.addEventListener('message', (event) => {
  if (event.data.type === 'zoom-factor') {
    setZoom(event.data.factor)
    setZoomControlsOpen(true)

    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = window.setTimeout(() => {
      setZoomControlsOpen(false)
    }, CLOSE_TIMEOUT)
  }
})

setZoomControlsOpen.watch(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})

export const updateDesktopZoom = () => {
  // eslint-disable-next-line effector/no-getState
  const currentZoom = $zoomFactor.getState()

  window.chSetZoom?.(currentZoom)
}

// update desktop if we change controls from CUI
resetZoom.watch(updateDesktopZoom)
increaseZoom.watch(updateDesktopZoom)
decreaseZoom.watch(updateDesktopZoom)
